/**
 * Authoritative Normal-mode simulation on a fixed reference canvas (800×600).
 * Positions are normalized 0–1 (x/width, y/height) for network transport.
 */

import type { MpObstacleState } from "./protocol.js";

export const MP_REF_W = 800;
export const MP_REF_H = 600;
export const MP_REF_MIN = Math.min(MP_REF_W, MP_REF_H);
/** Highway sim layout scale (matches server MpHighwaySim). */
export const MP_HIGHWAY_LAYOUT_SCALE = MP_REF_MIN / 600;
export const MP_SCORE_PER_SEC = 60;
export const MP_SPAWN_EVERY_SCORE = 500;
export const MP_SPAWN_BATCH = 50;
export const MP_BALL_BASE_SPEED = 0.8;
export const MP_BALL_RADIUS_PX = 14;
export const MP_CURSOR_RADIUS_PX = 5;
export const MP_SPAWN_CLEARANCE_PX = 40;
export const MP_BOUNCE_BOOST = 1.08;

export type MpSimObstacle = MpObstacleState;

export type MpSimPlayer = {
  id: string;
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  alive: boolean;
};

/** Seeded PRNG (mulberry32). */
export function mpCreateRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pxToNormX(px: number): number {
  return px / MP_REF_W;
}
function pxToNormY(px: number): number {
  return px / MP_REF_H;
}
function normToPxX(nx: number): number {
  return nx * MP_REF_W;
}
function normToPxY(ny: number): number {
  return ny * MP_REF_H;
}

function layoutScale(): number {
  return MP_REF_MIN / 600;
}

function ballRadiusNorm(radiusPx?: number): number {
  const px = radiusPx ?? MP_BALL_RADIUS_PX;
  return (px * layoutScale()) / MP_REF_MIN;
}

function cursorRadiusNorm(): number {
  return (MP_CURSOR_RADIUS_PX * layoutScale()) / MP_REF_MIN;
}

function minSpawnDistPx(ballRNorm: number): number {
  const ballPx = ballRNorm * MP_REF_MIN;
  return MP_CURSOR_RADIUS_PX * layoutScale() + ballPx + MP_SPAWN_CLEARANCE_PX * layoutScale();
}

function distNormPx(nx1: number, ny1: number, nx2: number, ny2: number): number {
  const dx = (nx1 - nx2) * MP_REF_W;
  const dy = (ny1 - ny2) * MP_REF_H;
  return Math.hypot(dx, dy);
}

function isClearOfPlayers(
  nx: number,
  ny: number,
  avoidNorm: Array<{ x: number; y: number }>,
  minPx: number,
): boolean {
  for (const p of avoidNorm) {
    if (distNormPx(nx, ny, p.x, p.y) < minPx) return false;
  }
  return true;
}

function spawnOne(
  rng: () => number,
  speedNorm: number,
  radiusPx: number | undefined,
  avoidNorm: Array<{ x: number; y: number }>,
): MpSimObstacle {
  const r = ballRadiusNorm(radiusPx);
  const minPx = minSpawnDistPx(r);
  let x = rng();
  let y = rng();
  let placed = false;
  for (let attempt = 0; attempt < 120; attempt++) {
    const nx = rng();
    const ny = rng();
    if (isClearOfPlayers(nx, ny, avoidNorm, minPx)) {
      x = nx;
      y = ny;
      placed = true;
      break;
    }
  }
  if (!placed) {
    x = rng();
    y = rng();
  }
  const vx = (rng() * 2 - 1) * speedNorm;
  const vy = (rng() * 2 - 1) * speedNorm;
  return { x, y, vx, vy, r };
}

function updateObstacle(o: MpSimObstacle, dt: number, bounceBoost = MP_BOUNCE_BOOST): void {
  const fs = dt * 60;
  const r = o.r;
  o.x += o.vx * fs;
  o.y += o.vy * fs;

  const minX = r;
  const maxX = 1 - r;
  const minY = r;
  const maxY = 1 - r;
  let bounced = false;

  if (o.x < minX) {
    o.x = minX;
    o.vx *= -1;
    bounced = true;
  } else if (o.x > maxX) {
    o.x = maxX;
    o.vx *= -1;
    bounced = true;
  }
  if (o.y < minY) {
    o.y = minY;
    o.vy *= -1;
    bounced = true;
  } else if (o.y > maxY) {
    o.y = maxY;
    o.vy *= -1;
    bounced = true;
  }
  if (bounced) {
    o.vx *= bounceBoost;
    o.vy *= bounceBoost;
  }
}

function hitAt(o: MpSimObstacle, px: number, py: number): boolean {
  const cr = cursorRadiusNorm();
  const dx = (px - o.x) * MP_REF_W;
  const dy = (py - o.y) * MP_REF_H;
  const dist = Math.hypot(dx, dy);
  return dist < o.r * MP_REF_MIN + cr * MP_REF_MIN;
}

function distPointToSegPx(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-10) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = x1 + t * dx;
  const qy = y1 + t * dy;
  return Math.hypot(px - qx, py - qy);
}

function hitSweep(o: MpSimObstacle, nx0: number, ny0: number, nx1: number, ny1: number): boolean {
  const crPx = cursorRadiusNorm() * MP_REF_MIN;
  const ballPx = o.r * MP_REF_MIN;
  const sumR = crPx + ballPx;
  const bx = o.x * MP_REF_W;
  const by = o.y * MP_REF_H;
  const x0 = nx0 * MP_REF_W;
  const y0 = ny0 * MP_REF_H;
  const x1 = nx1 * MP_REF_W;
  const y1 = ny1 * MP_REF_H;
  return distPointToSegPx(bx, by, x0, y0, x1, y1) < sumR;
}

export class MpNormalSim {
  seed: number;
  rng: () => number;
  obstacles: MpSimObstacle[] = [];
  score = 0;
  elapsed = 0;
  lastSpawnScore = 0;
  tick = 0;
  private scorePerSec: number;
  private spawnEveryScore: number;
  private spawnBatch: number;
  private ballBaseSpeed: number;
  private bounceBoost: number;
  private ballRadiusPx: number;
  private ballsEnabled: boolean;

  constructor(seed: number, options: Partial<{
    scorePerSec: number;
    spawnEveryScore: number;
    spawnBatch: number;
    ballBaseSpeed: number;
    bounceBoost: number;
    ballRadiusPx: number;
    ballsEnabled: boolean;
  }> = {}) {
    this.seed = seed;
    this.rng = mpCreateRng(seed);
    this.scorePerSec = options.scorePerSec ?? MP_SCORE_PER_SEC;
    this.spawnEveryScore = options.spawnEveryScore ?? MP_SPAWN_EVERY_SCORE;
    this.spawnBatch = options.spawnBatch ?? MP_SPAWN_BATCH;
    this.ballBaseSpeed = options.ballBaseSpeed ?? MP_BALL_BASE_SPEED;
    this.bounceBoost = options.bounceBoost ?? MP_BOUNCE_BOOST;
    this.ballRadiusPx = options.ballRadiusPx ?? MP_BALL_RADIUS_PX;
    this.ballsEnabled = options.ballsEnabled !== false;
  }

  private alivePlayerPositions(players: MpSimPlayer[]): Array<{ x: number; y: number }> {
    return players.filter((p) => p.alive).map((p) => ({ x: p.x, y: p.y }));
  }

  private ballR(): number {
    return ballRadiusNorm(this.ballRadiusPx);
  }

  step(dt: number, players: MpSimPlayer[]): void {
    this.elapsed += dt;
    this.score += this.scorePerSec * dt;
    this.tick++;

    const speedNorm = (this.ballBaseSpeed * layoutScale()) / MP_REF_MIN;
    const avoid = this.alivePlayerPositions(players);
    if (this.ballsEnabled) {
      if (this.obstacles.length === 0) {
        this.obstacles.push(spawnOne(this.rng, speedNorm, this.ballRadiusPx, avoid));
      }
      while (this.score - this.lastSpawnScore >= this.spawnEveryScore) {
        this.lastSpawnScore += this.spawnEveryScore;
        for (let i = 0; i < this.spawnBatch; i++) {
          this.obstacles.push(spawnOne(this.rng, speedNorm, this.ballRadiusPx, avoid));
        }
      }
    }

    for (const o of this.obstacles) {
      updateObstacle(o, dt, this.bounceBoost);
    }

    for (const p of players) {
      if (!p.alive) continue;
      for (const o of this.obstacles) {
        if (hitSweep(o, p.prevX, p.prevY, p.x, p.y)) {
          p.alive = false;
          break;
        }
      }
    }
  }

  snapshot(players: MpSimPlayer[]): {
    tick: number;
    score: number;
    obstacles: MpObstacleState[];
    players: Array<{ id: string; x: number; y: number; alive: boolean }>;
  } {
    return {
      tick: this.tick,
      score: this.score,
      obstacles: this.obstacles.map((o) => ({ ...o })),
      players: players.map((p) => ({ id: p.id, x: p.x, y: p.y, alive: p.alive })),
    };
  }
}

/** Convert normalized coords to canvas pixels. */
export function mpNormToCanvas(nx: number, ny: number, cw: number, ch: number): { x: number; y: number } {
  return { x: nx * cw, y: ny * ch };
}

/** Convert canvas pixels to normalized coords. */
export function mpCanvasToNorm(x: number, y: number, cw: number, ch: number): { x: number; y: number } {
  if (cw <= 0 || ch <= 0) return { x: 0.5, y: 0.5 };
  return { x: clamp01(x / cw), y: clamp01(y / ch) };
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Draw obstacle on canvas using reference radii scaled to viewport. */
export function mpDrawObstacle(
  ctx: CanvasRenderingContext2D,
  o: MpObstacleState,
  cw: number,
  ch: number,
  color: string,
): void {
  const ls = Math.min(cw, ch) / MP_REF_MIN;
  const x = o.x * cw;
  const y = o.y * ch;
  const r = o.r * MP_REF_MIN * ls;
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
